import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  useTheme,
} from '@mui/material';
import { Column, Row } from 'Components/Grid';
import { Controller } from 'react-hook-form';
import { ButtonForm } from 'Components/Form/styles';
import { useQueries } from 'react-query';
import { getAssetsShort } from 'services/assets-service';
import { getSystemsShort } from 'services/systems-service';
import { getProcessesShort } from 'services/processes-service';
import { getSelectedContent } from 'utils/select_utils';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { DotsLoader } from 'Components/DotsLoader';
import { getStyles, MenuProps } from './helpers';
import { InputLoader } from 'Components/PageLoader/Input';
import { useTranslation } from 'react-i18next';

export const Vinculos = ({ handleBack, handleComplete, control, handleSubmit, loading }) => {
  const { t } = useTranslation(['asm']);
  const selectedTenant = useSelector(useAuthTenant);
  const theme = useTheme();

  const onSubmit = () => {
    handleComplete();
  };

  const queries = useQueries([
    {
      queryKey: ['getProcesses'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getProcessesShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getSystems'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getSystemsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getAssets'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAssetsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
  ]);

  const [
    { data: processes, isLoading: processLoading, isError: processError },
    { data: systems, isLoading: systemLoading, isError: systemError },
    { data: assets, isLoading: assetLoading, isError: assetError },
  ] = queries;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row justifyContent="center">
       <Column xs={12} className="d-flex">
        {!processLoading && !processError && (
          <Controller
            control={control}
            name="processes"
            render={({ field: { onChange, value }, fieldState }) => {
              const safeValue = Array.isArray(value) ? value : [];

              const handleDelete = (selected) => {
                const newValue = safeValue.filter((v) => v._id !== selected._id);
                onChange(newValue);
              };

              return (
                <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                  <InputLabel id="processes-label" shrink>
                    {t('asm:areas.form.linkedProcesses')}
                  </InputLabel>

                  <Select
                    labelId="processes-label"
                    id="processes"
                    multiple
                    notched
                    value={getSelectedContent(processes, safeValue)}
                    onChange={(e) => onChange(e.target.value)}
                    input={<OutlinedInput id="processes-chip" label={t('asm:areas.form.linkedProcesses')} />}
                    renderValue={(selectedProcesses) => (
                      <Box
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        onMouseDown={(e) => e.stopPropagation()} // não abrir o menu ao interagir com chips
                      >
                        {selectedProcesses?.map((selectedValue) => (
                          <Chip
                            key={`process-chip-${selectedValue._id}`}
                            label={selectedValue.name}
                            variant="outlined"
                            color="primary"
                            onMouseDown={(e) => e.stopPropagation()}
                            onDelete={(e) => {
                              e.stopPropagation();
                              handleDelete(selectedValue);
                            }}
                            sx={{
                              width: 'fit-content',
                              display: selectedValue.archived ? 'none' : '',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {processes?.map((item) => (
                      <MenuItem
                        key={`process-${item._id}`}
                        value={item}
                        style={{
                          
                          ...getStyles(item._id, safeValue.map((v) => v._id), theme),
                          display: item.archived ? 'none' : '',
                        }}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                </FormControl>
              );
            }}
          />
        )}

        {processLoading && !processError && (
          <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
            <InputLabel id="processes-label" shrink>
              {t('asm:areas.form.linkedProcesses')}
            </InputLabel>
            <InputLoader />
          </FormControl>
        )}
      </Column>
      </Row>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          {!systemLoading && !systemError && (
            <Controller
              control={control}
              name="systems"
              render={({ field: { onChange, value }, fieldState }) => {
                const safeValue = Array.isArray(value) ? value : [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                const visibleSystems = systems?.filter(s => !s.archived) ?? [];

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="systems-label" shrink>
                      {t('asm:areas.form.linkedSystems')}
                    </InputLabel>

                    <Select
                      labelId="systems-label"
                      id="systems"
                      multiple
                      notched
                      value={getSelectedContent(visibleSystems, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="systems-chip" label={t('asm:areas.form.linkedSystems')} />}
                      renderValue={(selectedSystems) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {selectedSystems?.map((selectedValue) => (
                            <Chip
                              key={selectedValue._id}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue);
                              }}
                              sx={{
                                width: 'fit-content',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleSystems.map((item) => (
                        <MenuItem
                          key={item._id}
                          value={item}
                          style={{
                            ...getStyles(item._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <FormHelperText>
                      {fieldState.error ? fieldState.error.message : ''}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          {systemLoading && !systemError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="systems-label" shrink>
                {t('asm:areas.form.linkedSystems')}
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row>
        <Column xs={12} className="d-flex">
          {!assetLoading && !assetError && (
            <Controller
              control={control}
              name="assets"
              render={({ field: { onChange, value }, fieldState }) => {
                // 🔒 garante que seja array
                const safeValue = Array.isArray(value) ? value : [];

                // opcional: já filtra arquivados para não renderizar
                const visibleAssets = assets?.filter(a => !a.archived) ?? [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="assets-label" shrink>
                      {t('asm:areas.form.linkedAssets')}
                    </InputLabel>

                    <Select
                      labelId="assets-label"
                      id="assets"
                      multiple
                      notched
                      value={getSelectedContent(visibleAssets, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="assets-chip" label={t('asm:areas.form.linkedAssets')} />}
                      renderValue={(selectedAssets) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()} // evita abrir o menu ao interagir com chips
                        >
                          {selectedAssets?.map((selectedValue) => (
                            <Chip
                              key={selectedValue._id}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()} // impede abrir menu ao clicar no "X"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue); // remove só este
                              }}
                              sx={{
                                width: 'fit-content',
                                // se preferir manter a lógica por chip em vez de filtrar na origem:
                                // display: selectedValue.archived ? 'none' : '',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleAssets.map((item) => (
                        <MenuItem
                          key={item._id}
                          value={item}
                          style={{
                            // passa lista de IDs selecionados para destacar corretamente
                            ...getStyles(item._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <FormHelperText>
                      {fieldState.error ? fieldState.error.message : ''}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          {assetLoading && !assetError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="assets-label" shrink>
                {t('asm:areas.form.linkedAssets')}
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row justifyContent="end" alignItems="center">
        <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
          <ButtonForm secondary id="cancel" label={t('asm:areas.form.cancel')} onClick={handleBack}></ButtonForm>
          <ButtonForm label={loading ? <DotsLoader /> : t('asm:areas.form.save')} type="submit" />
        </Column>
      </Row>
    </form>
  );
};
